import React, { useState } from 'react';
import { Table, Form, Button, Modal } from 'react-bootstrap';

const DOWNLOAD_FILE_NAME = 'forest_table_export.txt';

export default function ForestTable({ parsedData, setParsedData, outcomeType, intervention, comparator }) {
  const [showModal, setShowModal] = useState(false);
  const [currentThought, setCurrentThought] = useState('');
  const [currentStudyName, setCurrentStudyName] = useState('');
  const [downloading, setDownloading] = useState(false); // feedback for button

  const handleChange = (studyId, field, value) => {
    setParsedData((prev) =>
      prev.map((entry) =>
        entry.id === studyId ? { ...entry, [field]: value } : entry
      )
    );
  };

  const handleShowThought = (entry) => {
    setCurrentThought(entry.thought || 'No thought provided.');
    setCurrentStudyName(entry.name);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentThought('');
    setCurrentStudyName('');
  };

  const isBinary = outcomeType === 'binary';

  // Build YAML for all entries
  const buildYaml = () =>
    parsedData
      .map((entry) => {
        if (entry.outcome_type === 'binary') {
          return (
`- study: ${entry.name}
  outcome_type: binary
  intervention:
    events: ${entry.intervention_events}
    total: ${entry.intervention_total}
  comparator:
    events: ${entry.comparator_events}
    total: ${entry.comparator_total}`
          );
        }

        return (
`- study: ${entry.name}
  outcome_type: continuous
  intervention:
    mean: ${entry.intervention_mean}
    sd: ${entry.intervention_sd}
    total: ${entry.intervention_group_size}
  comparator:
    mean: ${entry.comparator_mean}
    sd: ${entry.comparator_sd}
    total: ${entry.comparator_group_size}`
        );
      })
      .join('\n\n');

  // --------------------------
  //   DOWNLOAD TXT FUNCTION
  // --------------------------
  const handleDownloadTxt = () => {
    const txt = buildYaml();
    const blob = new Blob([txt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = DOWNLOAD_FILE_NAME;
    link.click();

    URL.revokeObjectURL(url);

    setDownloading(true);
    setTimeout(() => setDownloading(false), 1500); // same feedback as before
  };

  return (
    <>
      <Table bordered hover responsive className="mt-4">
        <thead>
          <tr>
            <th rowSpan="2">Study Name</th>
            <th rowSpan="2">Outcome Type</th>
            <th colSpan={isBinary ? 2 : 3}>{intervention}</th>
            <th colSpan={isBinary ? 2 : 3}>{comparator}</th>

            <th rowSpan="2" className="text-center">
              <Button
                className="logo-color"
                size="sm"
                onClick={handleDownloadTxt}
                title="Download TXT"
              >
                {downloading ? "✓ Done!" : "Export 📋"}
              </Button>
            </th>
          </tr>

          <tr>
            {isBinary ? (
              <>
                <th>Events</th>
                <th>Total</th>
                <th>Events</th>
                <th>Total</th>
              </>
            ) : (
              <>
                <th>Mean</th>
                <th>SD</th>
                <th>Total</th>
                <th>Mean</th>
                <th>SD</th>
                <th>Total</th>
              </>
            )}
          </tr>
        </thead>

        <tbody>
          {parsedData.map((entry) => (
            <tr key={entry.id}>
              <td>{entry.name}</td>
              <td>{entry.outcome_type}</td>

              {isBinary ? (
                <>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.intervention_events}
                      onChange={(e) =>
                        handleChange(entry.id, 'intervention_events', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.intervention_total}
                      onChange={(e) =>
                        handleChange(entry.id, 'intervention_total', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.comparator_events}
                      onChange={(e) =>
                        handleChange(entry.id, 'comparator_events', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.comparator_total}
                      onChange={(e) =>
                        handleChange(entry.id, 'comparator_total', e.target.value)
                      }
                    />
                  </td>
                </>
              ) : (
                <>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.intervention_mean}
                      onChange={(e) =>
                        handleChange(entry.id, 'intervention_mean', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.intervention_sd}
                      onChange={(e) =>
                        handleChange(entry.id, 'intervention_sd', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.intervention_group_size}
                      onChange={(e) =>
                        handleChange(entry.id, 'intervention_group_size', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.comparator_mean}
                      onChange={(e) =>
                        handleChange(entry.id, 'comparator_mean', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.comparator_sd}
                      onChange={(e) =>
                        handleChange(entry.id, 'comparator_sd', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.comparator_group_size}
                      onChange={(e) =>
                        handleChange(entry.id, 'comparator_group_size', e.target.value)
                      }
                    />
                  </td>
                </>
              )}

              <td>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleShowThought(entry)}
                >
                  Reasoning
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="logo-color text-white">
          <Modal.Title>Reasoning for: {currentStudyName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{currentThought}</pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
